import * as React from 'react';
import moment from 'moment/moment';
import { IQuotation } from 'app/shared/model/quotation.model';
import { POA_CONSTANTS, POA_STYLE_RULES } from 'app/entities/quotation/components/poa-utils';
import { IIban } from 'app/shared/model/iban.model';

type Props = {
  captureRef?: any;
  quotationEntity: IQuotation;
  poaDate?: string;
  ibanEntity: IIban;
  showPageNumber?: boolean;
};

export const PoaFormPrint = (props: Props) => {
  const { captureRef, quotationEntity, poaDate, ibanEntity, showPageNumber = true } = props;

  React.useEffect(() => {
    const printContent = document.getElementById('poaContentToPrint');
    if (!printContent) return;

    POA_STYLE_RULES.forEach(({ selector, styles }) => {
      const elements = printContent.querySelectorAll(selector);
      elements.forEach(element => {
        for (const property in styles) {
          if (Object.prototype.hasOwnProperty.call(styles, property)) {
            (element as HTMLElement).style.setProperty(property, styles[property], 'important');
          }
        }
      });
    });
  }, [quotationEntity, ibanEntity]);

  const formatDate = () => {
    if (poaDate) return moment(poaDate).format('DD-MMMM-YYYY');
    return moment().format('DD-MMMM-YYYY');
  };

  return (
    <>
      <div
        ref={captureRef}
        id="poaContentToPrint"
        dir="ltr"
        style={{
          position: 'relative',
          width: '794px',
          minHeight: '1123px',
          overflow: 'hidden',
          boxShadow: '0 0 40px rgba(0, 0, 0, 0.2)',
          backgroundColor: '#ffffff',
          margin: '20px auto',
          padding: '40px 48px',
          direction: 'ltr !important' as any,
          isolation: 'isolate',
        }}
      >
        {/* Header */}
        <div className="poa-header">
          <div className="poa-header-section">
            <img src="../content/images/img.png" width="240px" alt="Logo" />
          </div>
        </div>

        {/* Date and Bank Name Section */}
        <div className="poa-date-section">
          <table className="poa-date-table">
            <tbody>
              <tr>
                <td className="date-label">
                  {POA_CONSTANTS.DATE}: <strong>{formatDate()}</strong>
                </td>
                <td className="date-label-ar arabic-content">
                  {POA_CONSTANTS.DATE_AR}: <strong>{formatDate()}</strong>
                </td>
              </tr>
              <tr>
                <td className="bank-name">
                  <strong>{quotationEntity?.bank?.name || POA_CONSTANTS.BANK_COMPANY_NAME}</strong>
                </td>
                <td className="bank-name-ar arabic-content">
                  <strong>{quotationEntity?.bank?.name || POA_CONSTANTS.BANK_COMPANY_NAME_AR}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Greeting Section */}
        <div className="poa-greeting-section">
          <table className="poa-greeting-table">
            <tbody>
              <tr>
                <td className="greeting-en">{POA_CONSTANTS.GREETING}</td>
                <td className="greeting-ar arabic-content">{POA_CONSTANTS.GREETING_AR}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Title Section */}
        <div className="poa-title-section">
          <div className="title-ar arabic-content">
            <strong>{POA_CONSTANTS.TITLE_AR}</strong>
          </div>
          <div className="title-en">
            <strong>{POA_CONSTANTS.TITLE}</strong>
          </div>
        </div>

        {/* Body Content */}
        <div className="poa-body-section">
          <table className="poa-body-table">
            <tbody>
              <tr>
                <td className="body-en" dangerouslySetInnerHTML={{ __html: POA_CONSTANTS.BODY_EN }} />
                <td className="body-ar arabic-content" dangerouslySetInnerHTML={{ __html: POA_CONSTANTS.BODY_AR }} />
              </tr>
            </tbody>
          </table>
        </div>

        {/* Customer Details Table */}
        <div className="poa-details-section">
          <table className="poa-details-table">
            <tbody>
              <tr>
                <td className="details-label-cell">{POA_CONSTANTS.CUSTOMER_NAME}:</td>
                <td className="details-value-cell">
                  {quotationEntity?.commodity?.ownership === 'Local' && quotationEntity?.customer?.nameArabic
                    ? quotationEntity?.customer?.nameArabic
                    : quotationEntity?.customer?.name || ''}
                </td>
                <td className="details-arabic-label">{POA_CONSTANTS.CUSTOMER_NAME_AR}:</td>
              </tr>
              <tr>
                <td className="details-label-cell">{POA_CONSTANTS.IBAN_NUMBER}:</td>
                <td className="details-value-cell">{ibanEntity?.ibanNumber || ''}</td>
                <td className="details-arabic-label">{POA_CONSTANTS.IBAN_NUMBER_AR}:</td>
              </tr>
              <tr>
                <td className="details-label-cell">{POA_CONSTANTS.CIVIL_ID}:</td>
                <td className="details-value-cell">{quotationEntity?.customer?.ic || ''}</td>
                <td className="details-arabic-label">{POA_CONSTANTS.CIVIL_ID_AR}:</td>
              </tr>
              <tr>
                <td className="details-label-cell">{POA_CONSTANTS.MOBILE_NUMBER}:</td>
                <td className="details-value-cell">{quotationEntity?.customer?.tel || ''}</td>
                <td className="details-arabic-label">{POA_CONSTANTS.MOBILE_NUMBER_AR}:</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signature Section */}
        <div className="poa-signature-section">
          <div className="signature-label">
            {POA_CONSTANTS.SIGNATURE} / <span className="arabic-content">{POA_CONSTANTS.SIGNATURE_AR}</span>
          </div>
          <div className="signature-line"></div>
        </div>

        {/* Footer with Pagination */}
        {showPageNumber && (
          <div className="poa-footer">
            <div className="poa-pagination">1</div>
          </div>
        )}
      </div>
    </>
  );
};
